import { PlusIcon } from "@heroicons/react/solid"
import { Breadcrumbs } from "components/breadcrumbs"
import Button from "components/button"
import { Link } from "react-router-dom"
import { useTable, useUpdate } from "@refinedev/core"
import { useMemo, useState } from "react"
import Pagination from "components/pagination"
import { FullScreenLoading } from "components/fullScreenLoading"
import toast from "react-hot-toast"
import { ContactCard, ContactTable, Filter } from "./components"
import { ContactStatus, IContactItem } from "interfaces"
import { HistoryLodModal } from "components/historyLogModal"
import { isHaveActionPermission } from "utills/helpers"

const INITIAL_HISOTRY_MODAL_CONFIG = {
  open: false,
  id: "",
}

export const ContactList: React.FC = () => {
  const { mutate: updateMutation, isLoading: updateMutationIsLoading } = useUpdate();
  const [historyModalConfig, setHistoryModalConfig] = useState(INITIAL_HISOTRY_MODAL_CONFIG);

  const {
    tableQuery,
    filters,
    setFilters,
  } = useTable<IContactItem>({
    resource: "cs/support-center/contacts",
    syncWithLocation: true,
    pagination: {
      mode: "off",
    },
    meta: {
      resource: "data",
    },
  });

  const currentFilterValues = useMemo(() => {
    const logicalFilters = filters.flatMap((item) =>
      "field" in item ? item : []
    );

    return {
      q: logicalFilters.find((item) => item.field === "q")?.value || "",
      category_id: logicalFilters.find((item) => item.field === "category_id")?.value || "",
      status: logicalFilters.find((item) => item.field === "status ")?.value || "",
    };
  }, [filters]);

  const _handleChangeStatus = (id: string, status: ContactStatus) => {
    if (!isHaveActionPermission("contact", "cs", "edit")) return;
    updateMutation({
      resource: "cs/support-center/contacts",
      id,
      meta: {
        URLSuffix: status,
        ignoreResourceWrapper: true
      },
      values: {},
    },
    {
      onSuccess: () => {
        toast.success("Update successfully");
      },
    });
  };

  const _handlePageChanged = (page: number) => {
    const updatedFilters = [...filters].flatMap(item => "field" in item ? item: []);
    const pageIndex = updatedFilters.findIndex(filter => filter.field === "page");
    if (pageIndex < 0) {
      updatedFilters.push({field: "page", operator: "eq", value: page})
    } else {
      updatedFilters[pageIndex].value = page;
    }

    setFilters(updatedFilters);
  }
  
  const _onCloseHistoryModal = () => {
    setHistoryModalConfig(INITIAL_HISOTRY_MODAL_CONFIG);
  }

  const _openHistoryModal = (id: string) => {
    setHistoryModalConfig({
      open: true,
      id: id
    })
  }
  
  return (
    <div className="wrapper">
      <Breadcrumbs />
      <div className="box-container">
        <Filter
          currentFilterValues={currentFilterValues}
          setFilters={setFilters}
        />
      </div>
      <div className="box-container">
        {!tableQuery.data?.data?.length &&
          !tableQuery.isFetching && (
            <div className="text-center">
              No data
            </div>
          )}
        {tableQuery?.data?.data && tableQuery?.data?.data.length > 0 ? (
          <>
            <div className="sm:hidden">
              <ContactCard
                data={tableQuery?.data?.data}
                pageSize={tableQuery?.data?.pageSize}
                currentPage={tableQuery?.data?.paging.current_page}
                _handleChangeStatus={_handleChangeStatus}
                _handleOpenHistoryModal={_openHistoryModal}
              />
            </div>
            <div className="hidden sm:block">
              <ContactTable
                data={tableQuery?.data?.data}
                pageSize={tableQuery?.data?.pageSize}
                currentPage={tableQuery?.data?.paging.current_page}
                _handleChangeStatus={_handleChangeStatus}
                _handleOpenHistoryModal={_openHistoryModal}
              />
            </div>
          </>
        ) : <></>}
        {tableQuery?.data?.paging ? (
          <div className="mt-6">
            <Pagination
              currentPage={tableQuery?.data?.paging.current_page}
              totalPage={tableQuery?.data?.paging.total_page}
              total={{
                totalItems: tableQuery?.data?.paging?.total_items ?? 0,
                total: tableQuery?.data?.paging.total_contacts
              }}
              onChange={_handlePageChanged}
            />
          </div>
        ) : <></>}
      </div>
      <HistoryLodModal
        resource={`cs/support-center/contacts/${historyModalConfig.id}/history`}
        open={historyModalConfig.open}
        onClose={_onCloseHistoryModal}
        key="contact-history-modal"
        id="cs-contact"
      />
      {(tableQuery.isLoading) || updateMutationIsLoading ? <FullScreenLoading /> : <></>}
    </div>
  )
}